// Active-workout store. Mirrors the legacy `onflow_active_workout`
// sessionStorage pattern: a single in-progress workout that survives a page
// refresh but does not leak across browser tabs (sessionStorage is per-tab).
//
// Why sessionStorage instead of just React state:
//  - The legacy app supports refresh-mid-set without losing data.
//  - Crash recovery: if the server save fails on Finish, the user can retry.
//
// Why not localStorage:
//  - Two open tabs would step on each other. sessionStorage is per-tab and
//    matches the legacy behaviour.
import type { Set as WorkoutSet, WorkoutExercise } from '@onflow/shared';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ActiveWorkout {
  title: string;
  startedAt: string; // ISO8601
  notes: string;
  exercises: WorkoutExercise[];
  // Optional link back to the routine that launched the workout, so we can
  // mark it as "last used" on Finish.
  routineId?: string;
  // Optional plan-linkage: populated when the workout was started from the
  // dashboard's DailyMissionCard "Log Workout" button on a day with a
  // planned trainingSession. Both fields persist on the Workout row at
  // Finish and feed M6 weekly review's adherence numerator.
  planId?: string;
  planSessionId?: string;
  // Per-exercise resting state for the rest-timer overlay. Tracking it on the
  // store (not local component state) keeps the timer alive when the user
  // navigates between tabs in the active session screen.
  rest: { remainingSeconds: number; targetSeconds: number } | null;
}

interface ActiveWorkoutActions {
  start(input: {
    title: string;
    exercises?: WorkoutExercise[];
    routineId?: string;
    planId?: string;
    planSessionId?: string;
  }): void;
  setTitle(title: string): void;
  setNotes(notes: string): void;
  addExercise(exerciseId: string, defaultRest: number): void;
  removeExercise(exerciseIdx: number): void;
  addSet(exerciseIdx: number): void;
  removeSet(exerciseIdx: number, setIdx: number): void;
  updateSet(exerciseIdx: number, setIdx: number, patch: Partial<WorkoutSet>): void;
  cycleSetType(exerciseIdx: number, setIdx: number): void;
  toggleSetCompleted(exerciseIdx: number, setIdx: number): void;
  startRestTimer(seconds: number): void;
  tickRestTimer(): void;
  skipRestTimer(): void;
  discard(): void;
}

interface ActiveWorkoutState {
  workout: ActiveWorkout | null;
}

const SET_TYPE_CYCLE: WorkoutSet['type'][] = ['normal', 'warmup', 'dropset', 'failure'];

function emptySet(setNumber: number): WorkoutSet {
  return {
    setNumber,
    type: 'normal',
    weight: 0,
    reps: 0,
    completed: false,
  };
}

export const useActiveWorkoutStore = create<ActiveWorkoutState & ActiveWorkoutActions>()(
  persist(
    (set, get) => ({
      workout: null,

      start({ title, exercises = [], routineId, planId, planSessionId }) {
        set({
          workout: {
            title,
            startedAt: new Date().toISOString(),
            notes: '',
            exercises,
            routineId,
            planId,
            planSessionId,
            rest: null,
          },
        });
      },

      setTitle(title) {
        const w = get().workout;
        if (!w) return;
        set({ workout: { ...w, title } });
      },

      setNotes(notes) {
        const w = get().workout;
        if (!w) return;
        set({ workout: { ...w, notes } });
      },

      addExercise(exerciseId, defaultRest) {
        const w = get().workout;
        if (!w) return;
        const next: WorkoutExercise = {
          exerciseId,
          notes: '',
          restTimerSeconds: defaultRest,
          sets: [emptySet(1)],
        };
        set({ workout: { ...w, exercises: [...w.exercises, next] } });
      },

      removeExercise(exerciseIdx) {
        const w = get().workout;
        if (!w) return;
        set({
          workout: {
            ...w,
            exercises: w.exercises.filter((_, i) => i !== exerciseIdx),
          },
        });
      },

      addSet(exerciseIdx) {
        const w = get().workout;
        if (!w) return;
        const ex = w.exercises[exerciseIdx];
        if (!ex) return;
        const last = ex.sets[ex.sets.length - 1];
        // Carry the last set's weight forward — matches the legacy behaviour
        // where adding a set defaults to the previous set's load.
        const carry: WorkoutSet = last
          ? { ...emptySet(ex.sets.length + 1), weight: last.weight, reps: last.reps }
          : emptySet(ex.sets.length + 1);
        const updated = { ...ex, sets: [...ex.sets, carry] };
        set({
          workout: {
            ...w,
            exercises: w.exercises.map((e, i) => (i === exerciseIdx ? updated : e)),
          },
        });
      },

      removeSet(exerciseIdx, setIdx) {
        const w = get().workout;
        if (!w) return;
        const ex = w.exercises[exerciseIdx];
        if (!ex) return;
        const filtered = ex.sets
          .filter((_, i) => i !== setIdx)
          // Renumber so set numbers stay contiguous after a delete.
          .map((s, i) => ({ ...s, setNumber: i + 1 }));
        const updated = { ...ex, sets: filtered };
        set({
          workout: {
            ...w,
            exercises: w.exercises.map((e, i) => (i === exerciseIdx ? updated : e)),
          },
        });
      },

      updateSet(exerciseIdx, setIdx, patch) {
        const w = get().workout;
        if (!w) return;
        const ex = w.exercises[exerciseIdx];
        if (!ex) return;
        const sets = ex.sets.map((s, i) => (i === setIdx ? { ...s, ...patch } : s));
        const updated = { ...ex, sets };
        set({
          workout: {
            ...w,
            exercises: w.exercises.map((e, i) => (i === exerciseIdx ? updated : e)),
          },
        });
      },

      cycleSetType(exerciseIdx, setIdx) {
        const w = get().workout;
        if (!w) return;
        const ex = w.exercises[exerciseIdx];
        const target = ex?.sets[setIdx];
        if (!ex || !target) return;
        const currentIdx = SET_TYPE_CYCLE.indexOf(target.type);
        const nextType = SET_TYPE_CYCLE[(currentIdx + 1) % SET_TYPE_CYCLE.length]!;
        get().updateSet(exerciseIdx, setIdx, { type: nextType });
      },

      toggleSetCompleted(exerciseIdx, setIdx) {
        const w = get().workout;
        if (!w) return;
        const ex = w.exercises[exerciseIdx];
        const target = ex?.sets[setIdx];
        if (!ex || !target) return;
        const nowCompleted = !target.completed;
        get().updateSet(exerciseIdx, setIdx, { completed: nowCompleted });
        // Auto-start the rest timer when a set is freshly completed.
        if (nowCompleted && ex.restTimerSeconds > 0) {
          get().startRestTimer(ex.restTimerSeconds);
        }
      },

      startRestTimer(seconds) {
        const w = get().workout;
        if (!w) return;
        set({
          workout: {
            ...w,
            rest: { remainingSeconds: seconds, targetSeconds: seconds },
          },
        });
      },

      tickRestTimer() {
        const w = get().workout;
        if (!w?.rest) return;
        const remaining = w.rest.remainingSeconds - 1;
        if (remaining <= 0) {
          set({ workout: { ...w, rest: null } });
        } else {
          set({ workout: { ...w, rest: { ...w.rest, remainingSeconds: remaining } } });
        }
      },

      skipRestTimer() {
        const w = get().workout;
        if (!w) return;
        set({ workout: { ...w, rest: null } });
      },

      discard() {
        set({ workout: null });
      },
    }),
    {
      name: 'onflow_active_workout',
      // Per-tab persistence so two tabs don't fight over the same session.
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
